// @ts-nocheck TODO

import {html} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import UI_MODES from "../ui_modes.js";
import {initDrag, removeDrag} from "./drag_utils.js";
import {annotationStyles} from "./Annotation.styles.js";
import {annotationFreeformStyles} from "./AnnotationFreeform.styles.js";

import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/rich-text-editor/RichTextEditor.js";
import "./AnnotationHeader.js";
import "./AnnotationFooter.js";

import Data, {annotationStub} from "../../../main/data/index.js";
import {Editor} from "@tiptap/core";
import Utils from "../../../main/utils/index.js";
import DOMPurify from "dompurify";
import * as diff from "htmldiff/src/htmldiff.js";
import * as entities from "entities";
import {MobxLitElement} from "@adobe/lit-mobx";
import Integration from "../../../integrations/integration/index.js";

@customElement("pn-annotation-freeform")
export class AnnotationFreeform extends MobxLitElement {
	@property({attribute: false}) annotation = {
		comment: null,
		uuid:    null
	};
	@property() topic = {};
	@property() createdByName = "";
	@property() onDeleteAnnotation: () => void;
	@property() onUpdateNote:       () => void;
	@property() isNew = false;
	@property() sortBy = "topic";
	@property() mode:               UI_MODES;
	@property() isEditable:         boolean;

	@state() x = 0;
	@state() y = 0;

	static styles = [annotationStyles, annotationFreeformStyles];

	firstUpdated() {
		if (this.mode === UI_MODES.SIDEBAR_DRAG) {
			initDrag(this, {
				annotationUuid: this.annotation.uuid,
				type:           "freeform",
				comment:        this.annotation.comment
			});
		}
	}

	updated(changedProperties) {
		// TODO: improve conditional
		if (changedProperties.has("mode") && this.mode !== UI_MODES.SIDEBAR_DRAG) {
			// const prevMode = changedProperties.get("mode");
			// console.log(prevMode, this.mode);
			removeDrag(this.annotation.uuid);
		}
	}

	onClickDiffBtn(event) {
		const li = event.target.closest(".annotation-freeform");
		const noteContainer = li.querySelector(".annotation-note-container");
		noteContainer.classList.toggle("show-ai-diff");

		const noteContent = this.annotation.comment ? this.annotation.comment : "";

		if (noteContainer.classList.contains("show-ai-diff")) {
			// Replace RTE with AI diff
			li.querySelector(".show-on-hover").innerHTML = "&nbsp;&mdash;&nbsp;hide differences";
			const originalDecoded = entities.decodeHTML(this.annotation?.ai?.comment ?? "");
			const aiDiff = diff(originalDecoded ?? "", noteContent);
			li.querySelector(".ai-diff").innerHTML = DOMPurify.sanitize(aiDiff);
		} else {
			// Replace AI diff with RTE
			li.querySelector(".show-on-hover").innerHTML = "&nbsp;&mdash;&nbsp;view differences";
		}
	};

	updateNewNote = async ({editor, event}: {editor: Editor | null; event: FocusEvent}) => {
		const comment = editor.getHTML().trim();

		if (editor.getText() === "") {
			// For freeform notes, we delete the whole annotation if the note is empty.
			this.onDeleteAnnotation();
		} else {
			// Create the new annotation object, persist it locally and remotely, and finally update the UI
			const stub = {
				version: "2",
				type:    "freeform",
				comment
			} as annotationStub;
			await Data.annotation.create(stub, Data.app.project.uuid, this.topic.uuid, true);
			this.onDeleteAnnotation();
		}
	};

	updateNote = ({editor, event}: {editor: Editor | null; event: FocusEvent}) => {
		// TODO:
		// if (!editable)
		// 	return;

		const comment = editor.getHTML().trim();

		// Remove empty freeform notes from the DOM
		if (editor.getText() === "")
			Data.annotation.delete(this.annotation.uuid, Data.app.project.uuid);
		else
			Data.annotation.update(this.annotation.uuid, Data.app.project.uuid, {comment});
	};

	onBlurEditor = ({editor, event}: {editor: Editor | null; event: FocusEvent}) => {
		if (this.isNew)
			this.updateNewNote({editor, event});
		else
			this.updateNote({editor, event});
	};

	render() {
		const id = this.isNew ? "new-freeform" : this.annotation.uuid;

		let isEdited = false;
		if (this.annotation.ai) {
			const current = this.annotation.comment;
			const original = this.annotation.ai.comment ?? current;
			const originalDecoded = entities.decodeHTML(original);
			isEdited = Utils.Document.normalizeHtml(originalDecoded) !== Utils.Document.normalizeHtml(current);
		}

		const canEditComment = this.mode === UI_MODES.SIDEBAR;

		return html`
			<div role="listitem"
				class="${classMap({
					"annotation-list-item":              true,
					"annotation-freeform":               true,
					"js-aa-topic-annotation":            true,
					"mode-sidebar":                      this.mode === UI_MODES.SIDEBAR,
					"mode-sidebar-drag":                 this.mode === UI_MODES.SIDEBAR_DRAG,
					"mode-sidebar-readonly":             this.mode === UI_MODES.SIDEBAR_READONLY,
					["integration-" + Integration.name]: Integration.isIntegration()
				})}"
				data-annotation-type="freeform"
				data-annotation-uuid="${this.annotation.uuid}"
				data-topic-uuid="${this.topic?.uuid}"
			>
				<pn-annotation-header
					.annotation="${this.annotation}"
					.topic="${this.topic}"
					sortBy="${this.isNew ? "topic" : this.sortBy}"
				></pn-annotation-header>

				${this.annotation.ai ? html`
					<div class="ai-identifier">
						<i class="icon-brain"></i>
						<span class="ai-label">Brainstorm with AI</span>
						${(isEdited && this.mode !== UI_MODES.SIDEBAR_DRAG) ? html`
							<pn-button
								class="btn-ai-edited"
								.onClick="${(event) => this.onClickDiffBtn(event)}"
								renderType="link"
							>
								(edited<span class="show-on-hover">&nbsp;&mdash;&nbsp;view differences</span>)
							</pn-button>
						` : null}
					</div>
				` : null}

				<div class="annotation-note-container">
					<div class="ai-diff"></div>
					<pn-rte
						.autofocus=${this.isNew}
						.blurOnEnter="${true}"
						.constructEditorImmediately="${this.isNew}"
						.content="${this.annotation.comment}"
						.editable="${canEditComment}"
						id="${`pn-annotation-editor-${id}`}"
						.isAnnotationNote="${true}"
						.onBlur=${this.onBlurEditor}
						.placeholder="${"+ Note"}"
						.showToolbarOnFocusOnly="${true}"
					></pn-rte>
				</div>

				<pn-annotation-footer
					.annotation="${this.annotation}"
					.isEditable=${this.isEditable}
					.loadProjectTopics="${this.loadProjectTopics}"
					.mode="${this.mode}"
					.onClickPageLink="${(event) => this.onClickPageLink(event, this.annotation.uuid)}"
					.onDeleteAnnotation="${this.onDeleteAnnotation}"
					.onUpdateAnnotationTopic="${this.onUpdateAnnotationTopic}"
					.projects="${this.projects}"
					.projectTopicsUrl="${this.projectTopicsUrl}"
					.topics="${this.topics}"
				></pn-annotation-footer>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-annotation-freeform": AnnotationFreeform;
	}
}
